import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ZoomIn } from 'lucide-react';
import Seo from '@/components/Seo';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SectionEyebrow from '@/components/SectionEyebrow';
import { FadeIn } from '@/components/motion-primitives';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { GUIDE_CHAPTERS } from '@/data/usageGuide';

/* =====================================================================
   UsageGuide (Nasıl Kullanılır) → route: /nasil-kullanilir
   Kayıttan sipariş ve sadakate kadar ekran görüntülü adım adım rehber.
   İçerik src/data/usageGuide.js'te; görsel 960/1800 WebP srcset ile gelir,
   tıklanınca büyük hali dialog'da açılır (üzerindeki notlar okunabilsin diye).
   ===================================================================== */

const IMG_DIR = '/img/rehber';
const IMG_WIDTH = 1800;

// Adım numaraları bölümler boyunca kesintisiz devam eder.
let counter = 0;
const CHAPTERS = GUIDE_CHAPTERS.map((chapter) => ({
  ...chapter,
  steps: chapter.steps.map((step) => ({ ...step, number: ++counter })),
}));
const TOTAL_STEPS = counter;

const srcFor = (image, width) => `${IMG_DIR}/${image}-${width}.webp`;

const UsageGuide = () => {
  const [zoomed, setZoomed] = useState(null);
  const [activeId, setActiveId] = useState(CHAPTERS[0].id);
  const tocRef = useRef(null);

  // Scroll-spy: üst kenarı header'ın biraz altını geçmiş son bölüm aktif sayılır.
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      let current = CHAPTERS[0].id;
      for (const { id } of CHAPTERS) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 140) current = id;
      }
      setActiveId(current);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(frame);
    };
  }, []);

  // Mobilde bölümler yatay şerit: aktif bölümü şeridin ortasına kaydır.
  useEffect(() => {
    const list = tocRef.current;
    const link = list?.querySelector('[aria-current]');
    if (!link || list.scrollWidth <= list.clientWidth) return;
    list.scrollTo({ left: link.offsetLeft - (list.clientWidth - link.offsetWidth) / 2, behavior: 'smooth' });
  }, [activeId]);

  return (
    <div className="min-h-screen bg-surface">
      <Seo
        title="Nasıl Kullanılır? — Tilki Otomasyon"
        description="Tilki Otomasyon adım adım kullanım rehberi: hesap kurulumu, personel ve ürün ekleme, masadan ve QR menüden sipariş, hesap kapatma, kampanya ve sadakat, stok ve destek talepleri."
        path="/nasil-kullanilir"
      />
      <Header />

      <main>
        {/* Hero */}
        <section className="bg-ink-strong pb-12 pt-16 sm:pb-14 sm:pt-20">
          <div className="container mx-auto px-4">
            <FadeIn className="mx-auto max-w-3xl text-center">
              <SectionEyebrow className="mb-4 border-white/15 bg-white/10 text-white/80">
                Kullanım Rehberi
              </SectionEyebrow>
              <h1 className="type-display-2 text-balance mb-4 !text-white">Tilki Otomasyon nasıl kullanılır?</h1>
              <p className="type-body-lg text-pretty mx-auto max-w-2xl !text-white/70">
                Hesabınızın açılmasından ilk siparişinize, QR menüden sadakat programına kadar {TOTAL_STEPS} adımda
                ekran görüntüleriyle anlattık.
              </p>
            </FadeIn>
          </div>
        </section>

        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 gap-10 py-12 sm:py-16 lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-14">
            {/* İçindekiler */}
            {/* Mobilde header altına yapışan yatay şerit, lg'de yan sütun */}
            <nav
              aria-label="Rehber bölümleri"
              className="sticky top-16 z-40 -mx-4 border-b border-hairline bg-surface/95 px-4 py-2 backdrop-blur lg:top-24 lg:mx-0 lg:self-start lg:border-0 lg:bg-transparent lg:p-0 lg:backdrop-blur-none"
            >
              <p className="mb-3 hidden font-inter text-xs font-semibold uppercase tracking-[0.14em] text-ink-muted lg:block">
                Bölümler
              </p>
              <ol
                ref={tocRef}
                className="relative flex gap-1 overflow-x-auto [scrollbar-width:none] lg:grid lg:grid-cols-1 lg:overflow-visible [&::-webkit-scrollbar]:hidden"
              >
                {CHAPTERS.map((chapter, i) => (
                  <li key={chapter.id} className="flex-shrink-0">
                    <a
                      href={`#${chapter.id}`}
                      aria-current={activeId === chapter.id ? 'location' : undefined}
                      className={`flex items-baseline gap-2.5 whitespace-nowrap rounded-lg px-3 py-2 font-inter text-sm transition-colors lg:whitespace-normal ${
                        activeId === chapter.id
                          ? 'bg-surface-2 font-semibold text-ink-strong'
                          : 'text-ink-muted hover:bg-surface-2 hover:text-ink-strong'
                      }`}
                    >
                      <span className="font-poppins text-xs font-semibold text-brand-600">{i + 1}.</span>
                      {chapter.title}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            {/* Bölümler ve adımlar */}
            <div className="max-w-[880px] space-y-20">
              {CHAPTERS.map((chapter, i) => (
                <section key={chapter.id} id={chapter.id} className="scroll-mt-32 lg:scroll-mt-24">
                  <FadeIn className="mb-10 border-b border-hairline pb-6">
                    <p className="mb-2 font-inter text-xs font-semibold uppercase tracking-[0.14em] text-brand-600">
                      Bölüm {i + 1}
                    </p>
                    <h2 className="type-h2 text-balance mb-2">{chapter.title}</h2>
                    <p className="type-body-lg">{chapter.summary}</p>
                  </FadeIn>

                  <ol className="space-y-14">
                    {chapter.steps.map((step) => (
                      <li key={step.image} id={`adim-${step.number}`} className="scroll-mt-32 lg:scroll-mt-24">
                        <FadeIn>
                          <div className="mb-4 flex items-start gap-3.5">
                            <span className="mt-0.5 inline-flex h-8 min-w-8 flex-shrink-0 items-center justify-center rounded-full bg-ink-strong px-2 font-poppins text-sm font-semibold text-white">
                              {step.number}
                            </span>
                            <div>
                              <p className="font-inter text-xs font-medium text-ink-muted">
                                Adım {step.number} / {TOTAL_STEPS}
                              </p>
                              <h3 className="type-h3 text-balance">{step.title}</h3>
                            </div>
                          </div>
                          <p className="mb-5 font-inter text-[15px] leading-relaxed text-ink-muted">{step.text}</p>
                          <button
                            type="button"
                            onClick={() => setZoomed(step)}
                            className="group relative block w-full overflow-hidden rounded-2xl border border-hairline bg-surface p-2 text-left shadow-card transition-shadow hover:shadow-card-lift focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
                            aria-label={`${step.title} ekran görüntüsünü büyüt`}
                          >
                            <img
                              src={srcFor(step.image, 960)}
                              srcSet={`${srcFor(step.image, 960)} 960w, ${srcFor(step.image, 1800)} 1800w`}
                              sizes="(min-width: 1024px) 880px, 100vw"
                              width={IMG_WIDTH}
                              height={step.height}
                              alt={step.title}
                              loading={step.number <= 1 ? 'eager' : 'lazy'}
                              decoding="async"
                              className="h-auto w-full rounded-xl"
                            />
                            <span className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-ink-strong/80 px-3 py-1.5 font-inter text-xs font-medium text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                              <ZoomIn className="h-3.5 w-3.5" aria-hidden="true" />
                              Büyüt
                            </span>
                          </button>
                        </FadeIn>
                      </li>
                    ))}
                  </ol>
                </section>
              ))}
            </div>
          </div>
        </div>

        {/* Kapanış CTA */}
        <section className="section-y border-t border-hairline bg-surface-2">
          <div className="container mx-auto px-4">
            <FadeIn className="mx-auto max-w-2xl text-center">
              <h2 className="type-h2 text-balance mb-3.5">Kendi işletmenizde denemek ister misiniz?</h2>
              <p className="type-body-lg mb-8">Ücretsiz demo talep edin, kurulumu birlikte yapalım.</p>
              <div className="flex flex-col justify-center gap-3 sm:flex-row">
                <Link to="/demo" className="modern-btn-large">
                  Ücretsiz Demo Al
                  <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
                </Link>
                <Link to="/iletisim" className="modern-btn-secondary">
                  İletişime Geç
                </Link>
              </div>
            </FadeIn>
          </div>
        </section>
      </main>

      <Footer />

      <Dialog open={Boolean(zoomed)} onOpenChange={(open) => !open && setZoomed(null)}>
        <DialogContent
          className="w-[96vw] max-w-[1600px] grid-cols-1 gap-3 bg-surface p-3 sm:p-4"
          aria-describedby={undefined}
        >
          {zoomed && (
            <>
              <DialogTitle className="pr-8 font-poppins text-base font-semibold text-ink-strong">
                Adım {zoomed.number}: {zoomed.title}
              </DialogTitle>
              <img
                src={srcFor(zoomed.image, 1800)}
                width={IMG_WIDTH}
                height={zoomed.height}
                alt={zoomed.title}
                className="mx-auto h-auto max-h-[calc(92vh-4rem)] w-auto max-w-full rounded-lg"
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsageGuide;
